import { useMemo, useRef, useState } from 'react';
import { ArrowLeft } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { useInAppHistoryStore } from '@/hooks/use-in-app-history';
import { getPageTitle } from '../hrm-top-bar/hrm-shell-breadcrumbs';

/** How long a press has to last to open Recent pages instead of going back. */
export const HOLD_TO_OPEN_MS = 500;
const RECENT_PAGES_SHOWN = 5;

/**
 * The top bar's Back button (HR Module Prototype, 2 Nov): steps back through the pages visited in
 * this tab. It is disabled until you have moved between pages. Pressing and holding it (500ms) or
 * right-clicking it opens "Recent pages", the last five pages, to jump back several steps at once.
 */
export const HistoryBackButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const entries = useInAppHistoryStore((state) => state.entries);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedByHold = useRef(false);

  // Most recent first; `steps` is how far back each one is.
  const previous = useMemo(
    () =>
      entries
        .slice(0, -1)
        .reverse()
        .map((entry, index) => ({ ...entry, steps: index + 1 })),
    [entries]
  );
  const titleOf = (pathname: string) => {
    const key = getPageTitle(pathname);
    return key ? t(key) : pathname;
  };

  const back = previous[0];
  const label = back ? t('BACK_TO_PAGE', { page: titleOf(back.pathname) }) : t('BACK');

  const clearHold = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-md text-muted-foreground hover:bg-muted"
          disabled={!back}
          aria-label={label}
          title={label}
          // The trigger opens on pointer-down by default; here a short press goes back, and only a
          // hold, a right-click or ArrowDown opens the list. preventDefault() skips Radix's own handler.
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            openedByHold.current = false;
            clearHold();
            holdTimer.current = setTimeout(() => {
              openedByHold.current = true;
              setIsMenuOpen(true);
            }, HOLD_TO_OPEN_MS);
          }}
          onPointerUp={clearHold}
          onPointerLeave={clearHold}
          onClick={(event) => {
            event.preventDefault();
            clearHold();
            if (openedByHold.current) {
              openedByHold.current = false;
              return;
            }
            if (back) navigate(-1);
          }}
          // Enter and Space go back like a click (the trigger would open the list); ArrowDown opens it.
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            if (back) navigate(-1);
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            clearHold();
            if (back) setIsMenuOpen(true);
          }}
        >
          <ArrowLeft className="size-[18px]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuLabel>{t('RECENT_PAGES')}</DropdownMenuLabel>
        {previous.slice(0, RECENT_PAGES_SHOWN).map((entry) => (
          <DropdownMenuItem key={entry.key} onSelect={() => navigate(-entry.steps)}>
            {titleOf(entry.pathname)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
